import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight, BookOpen, ClipboardList, FileText, Video, Loader2,
  Calendar, Download, Maximize2, File, Image as ImageIcon, X, StickyNote, BarChart3,
  Gamepad2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { getSignedFileUrl } from "@/lib/storage-utils";
import ParentAssignments from "@/components/parent/ParentAssignments";
import MonthlyChildReport from "@/components/reports/MonthlyChildReport";
import ActivityPlayer from "@/components/activities/ActivityPlayer";

const ChildLearning = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const nurseryId = profile?.nursery_id;

  const [selectedLesson, setSelectedLesson] = useState<any>(null);
  const [lessonFiles, setLessonFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fullscreenFile, setFullscreenFile] = useState<{ url: string; name: string; type?: string } | null>(null);

  // Escape to close fullscreen
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setFullscreenFile(null); };
    if (fullscreenFile) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreenFile]);

  // Fetch child
  const { data: child, isLoading: loadingChild } = useQuery({
    queryKey: ["parent-child-detail", childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("*, classes(name)")
        .eq("id", childId!)
        .eq("parent_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!childId && !!user,
  });

  // Fetch lessons for child's class
  const { data: lessons, isLoading: loadingLessons } = useQuery({
    queryKey: ["parent-child-lessons", child?.class_id, nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lessons")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .eq("class_id", child!.class_id!)
        .order("scheduled_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
    enabled: !!child?.class_id && !!nurseryId,
  });

  // Load files when selecting a lesson
  const handleSelectLesson = async (lesson: any) => {
    setSelectedLesson(lesson);
    setLoadingFiles(true);
    const { data: files } = await supabase
      .from("lesson_files")
      .select("*")
      .eq("lesson_id", lesson.id)
      .order("created_at");
    
    // Generate signed URLs for private files
    const filesWithUrls = await Promise.all(
      (files || []).map(async (f: any) => {
        const signedUrl = await getSignedFileUrl("lesson-files", f.file_url);
        return { ...f, signed_url: signedUrl || f.file_url };
      })
    );
    setLessonFiles(filesWithUrls);
    setLoadingFiles(false);
  };

  if (loadingChild) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!child) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/dashboard/parent")} className="gap-2">
          <ArrowRight className="w-4 h-4" />
          العودة
        </Button>
        <Card className="shadow-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            لم يتم العثور على بيانات الطفل
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/parent")}>
          <ArrowRight className="w-5 h-5" />
        </Button>
        <Avatar className="w-12 h-12">
          <AvatarImage src={child.photo_url || ""} />
          <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
            {child.full_name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-xl font-bold text-foreground">{child.full_name}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {child.classes && <Badge variant="outline" className="text-xs">{child.classes.name}</Badge>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="lessons" dir="rtl">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="lessons" className="gap-1.5 text-sm">
            <BookOpen className="w-4 h-4" />
            الدروس
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-1.5 text-sm">
            <StickyNote className="w-4 h-4" />
            المذكرة
          </TabsTrigger>
          <TabsTrigger value="assignments" className="gap-1.5 text-sm">
            <ClipboardList className="w-4 h-4" />
            الواجبات
          </TabsTrigger>
          <TabsTrigger value="activities" className="gap-1.5 text-sm">
            <Gamepad2 className="w-4 h-4" />
            الأنشطة التفاعلية
          </TabsTrigger>
          <TabsTrigger value="report" className="gap-1.5 text-sm">
            <BarChart3 className="w-4 h-4" />
            التقرير الشهري
          </TabsTrigger>
        </TabsList>

        {/* ─── Lessons Tab ─── */}
        <TabsContent value="lessons">
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Lessons list */}
            <Card className="shadow-card lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  قائمة الدروس
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loadingLessons ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  </div>
                ) : !lessons?.length ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">لا توجد دروس</div>
                ) : (
                  <ScrollArea className="max-h-[500px]">
                    <div className="divide-y">
                      {lessons.map((l) => (
                        <button
                          key={l.id}
                          className={`w-full text-right p-3 hover:bg-muted/50 transition-colors ${
                            selectedLesson?.id === l.id ? "bg-primary/5 border-r-2 border-primary" : ""
                          }`}
                          onClick={() => handleSelectLesson(l)}
                        >
                          <p className="font-medium text-foreground text-sm truncate">{l.title}</p>
                          {l.scheduled_at && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {format(new Date(l.scheduled_at), "dd MMM yyyy", { locale: ar })}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Lesson detail */}
            <Card className="shadow-card lg:col-span-2">
              <CardContent className="p-0">
                {!selectedLesson ? (
                  <div className="py-16 text-center">
                    <BookOpen className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">اختر درساً لعرض محتواه</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-4">
                    <div>
                      <h2 className="text-lg font-bold text-foreground">{selectedLesson.title}</h2>
                      {selectedLesson.scheduled_at && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(selectedLesson.scheduled_at), "EEEE dd MMMM yyyy", { locale: ar })}
                        </p>
                      )}
                    </div>

                    {selectedLesson.content && (
                      <div className="bg-muted/30 rounded-lg p-4">
                        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                          {selectedLesson.content}
                        </p>
                      </div>
                    )}

                    {/* Files */}
                    {loadingFiles ? (
                      <div className="flex justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      </div>
                    ) : lessonFiles.length > 0 ? (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-foreground">المرفقات</h3>
                        {lessonFiles.map((f: any) => {
                          const isPdf = f.file_type === "pdf";
                          const isVideo = f.file_type === "video";
                          const isImage = f.file_type === "image";
                          const isWord = f.file_type === "word" || f.file_name?.endsWith(".docx") || f.file_name?.endsWith(".doc");
                          const isPpt = f.file_name?.endsWith(".pptx") || f.file_name?.endsWith(".ppt");
                          const isOffice = isWord || isPpt;
                          const officeViewerUrl = isOffice ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(f.signed_url)}` : null;

                          const icon = isPdf || isWord ? <FileText className="w-4 h-4" />
                            : isPpt ? <FileText className="w-4 h-4" />
                            : isVideo ? <Video className="w-4 h-4" />
                            : isImage ? <ImageIcon className="w-4 h-4" />
                            : <File className="w-4 h-4" />;

                          return (
                            <div key={f.id} className="space-y-2">
                              {/* Inline preview */}
                              {isPdf && (
                                <div className="relative">
                                  <iframe src={f.signed_url} className="w-full h-[350px] rounded-lg border" title={f.file_name} />
                                  <Button
                                    variant="secondary"
                                    size="icon"
                                    className="absolute top-2 left-2 h-8 w-8 shadow-md"
                                    onClick={() => setFullscreenFile({ url: f.signed_url, name: f.file_name, type: "pdf" })}
                                  >
                                    <Maximize2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                              {isOffice && officeViewerUrl && (
                                <div className="relative">
                                  <iframe src={officeViewerUrl} className="w-full h-[350px] rounded-lg border" title={f.file_name} />
                                  <Button
                                    variant="secondary"
                                    size="icon"
                                    className="absolute top-2 left-2 h-8 w-8 shadow-md"
                                    onClick={() => setFullscreenFile({ url: officeViewerUrl, name: f.file_name, type: "office" })}
                                  >
                                    <Maximize2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                              {isVideo && (
                                <video controls className="w-full rounded-lg border max-h-[350px]" src={f.signed_url} />
                              )}
                              {isImage && (
                                <img src={f.signed_url} alt={f.file_name} className="w-full rounded-lg border max-h-[350px] object-contain bg-muted" />
                              )}

                              {/* Download link */}
                              <a
                                href={f.signed_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 rounded-lg border hover:bg-muted/50 transition-colors text-sm"
                              >
                                {icon}
                                <span className="flex-1 truncate">{f.file_name}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {isPdf ? "PDF" : isWord ? "Word" : isPpt ? "PowerPoint" : isVideo ? "فيديو" : isImage ? "صورة" : "ملف"}
                                </Badge>
                                <Download className="w-3.5 h-3.5 text-muted-foreground" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── Notes Tab ─── */}
        <TabsContent value="notes">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-primary" />
                مذكرة الدروس
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingLessons ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                </div>
              ) : !lessons?.filter(l => l.content).length ? (
                <div className="py-12 text-center">
                  <StickyNote className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">لا توجد ملاحظات للدروس</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[600px]">
                  <div className="divide-y">
                    {lessons.filter(l => l.content).map((l) => (
                      <div key={l.id} className="p-4 space-y-2 hover:bg-muted/20 transition-colors">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground text-sm">{l.title}</h3>
                          {l.scheduled_at && (
                            <span className="text-[11px] text-muted-foreground">
                              {format(new Date(l.scheduled_at), "dd MMM yyyy", { locale: ar })}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                          {l.content}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Assignments Tab ─── */}
        <TabsContent value="assignments">
          {nurseryId && (
            <ParentAssignments
              childIds={[child.id]}
              classIds={[child.class_id]}
              nurseryId={nurseryId}
            />
          )}
        </TabsContent>

        {/* ─── Activities Tab ─── */}
        <TabsContent value="activities">
          {nurseryId && selectedLesson ? (
            <ActivityPlayer
              lessonId={selectedLesson.id}
              childId={child.id}
              nurseryId={nurseryId}
            />
          ) : (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <Gamepad2 className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">اختر درساً أولاً من تبويب الدروس لعرض الأنشطة التفاعلية</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Monthly Report Tab ─── */}
        <TabsContent value="report">
          <MonthlyChildReport fixedChildId={child.id} fixedChildName={child.full_name} />
        </TabsContent>
      </Tabs>

      {/* Fullscreen file overlay */}
      {fullscreenFile && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between p-3 border-b bg-background">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm font-medium text-foreground truncate">{fullscreenFile.name}</span>
            </div>
            <div className="flex items-center gap-2">
              {fullscreenFile.type === "pdf" && (
                <Button size="sm" variant="outline" asChild>
                  <a href={fullscreenFile.url} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 ml-1" />
                    تحميل
                  </a>
                </Button>
              )}
              <Button size="icon" variant="ghost" onClick={() => setFullscreenFile(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <div className="flex-1">
            <iframe src={fullscreenFile.url} className="w-full h-full border-0" title={fullscreenFile.name} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildLearning;
